'use client';
import Slider from 'react-slick';
import { twMerge } from 'tailwind-merge';

import { IComponentReviewCarouselSection } from '@/types/cms';
import { faChevronLeft, faChevronRight, faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface CustomArrowProps {
  isRight?: boolean;
  onClick?: () => void;
}

const CustomArrow = ({ isRight, onClick }: CustomArrowProps) => (
  <button
    type="button"
    onClick={onClick}
    className={twMerge(
      'absolute top-1/2 -translate-y-1/2 flex items-center justify-center cursor-pointer z-10 outline-none size-8 text-black-500',
      isRight ? 'right-2' : 'left-2',
    )}
  >
    <FontAwesomeIcon className="size-6" icon={isRight ? faChevronRight : faChevronLeft} />
  </button>
);

const CustomPaging = () => (
  <div className="size-4 rounded-full border border-black-500 p-0.5 cursor-pointer hover:border-black-600">
    <figure className="size-full rounded-full" />
  </div>
);

interface ReviewProps {
  review: IComponentReviewCarouselSection['reviews'][number];
}

const Review = ({ review }: ReviewProps) => (
  <div>
    <div
      style={{
        backgroundColor: review.backgroud_color || '#F5F5F5',
      }}
      className="flex flex-col items-center px-16 pt-20 pb-32 gap-2.5"
    >
      <span className="font-bold text-5xl leading-none -mb-2">“</span>
      <p className="text-sm text-center md:text-base">{review.content}</p>
      <span className="font-bold text-5xl leading-none mt-2">”</span>
      <div className="flex gap-1">
        <FontAwesomeIcon icon={faStar} className="text-yellow-500 size-5" />
        <FontAwesomeIcon icon={faStar} className="text-yellow-500 size-5" />
        <FontAwesomeIcon icon={faStar} className="text-yellow-500 size-5" />
        <FontAwesomeIcon icon={faStar} className="text-yellow-500 size-5" />
        <FontAwesomeIcon icon={faStar} className="text-yellow-500 size-5" />
      </div>
      <span className="font-bold text-xl">{review.author}</span>
    </div>
  </div>
);

export const ReviewCarouselSection = ({
  reviews,
  transition_speed,
  time_between_transitions,
}: IComponentReviewCarouselSection) => {
  let autoplaySpeed = 5000;
  if (time_between_transitions && typeof time_between_transitions === 'number') {
    autoplaySpeed = time_between_transitions * 1000;
  }

  let speed = 500;
  if (transition_speed && typeof transition_speed === 'number') {
    speed = transition_speed * 1000;
  }

  return (
    <div className="font-helvetica text-black-500 my-16 px-6 md:px-10 lg:container lg:mx-auto">
      {reviews.length > 1 ? (
        <Slider
          dots={reviews.length > 1}
          customPaging={CustomPaging}
          dotsClass="absolute bottom-8 right-1/2 !flex gap-1.5 translate-x-1/2 [&>li.slick-active_figure]:!bg-black-500 z-10"
          arrows={reviews.length > 1}
          nextArrow={<CustomArrow isRight />}
          prevArrow={<CustomArrow />}
          slidesToScroll={1}
          slidesToShow={1}
          autoplay
          autoplaySpeed={autoplaySpeed}
          speed={speed}
        >
          {reviews.map((review, index) => (
            <Review key={index + review.author} review={review} />
          ))}
        </Slider>
      ) : reviews[0] ? (
        <Review review={reviews[0]} />
      ) : null}
    </div>
  );
};
